import * as React from "react";

import { cn } from "@/lib/utils";

export interface SegmentedControlOption {
  label: string;
  value: string;
}

interface SegmentedControlProps {
  /** Currently selected option value. */
  value: string;
  /** Called with the newly selected option value. */
  onChange: (value: string) => void;
  /** Options to render as segments (2+). */
  options: SegmentedControlOption[];
  className?: string;
  "aria-label"?: string;
}

/**
 * A small, self-contained segmented toggle: a bordered group of buttons where
 * the active option is filled. Used where a full radix `ToggleGroup` primitive
 * is overkill (e.g. a two-way "No / Yes" switch). Exposes a `radiogroup` /
 * `radio` a11y role so it reads as a single-select control.
 */
export const SegmentedControl: React.FC<SegmentedControlProps> = ({
  value,
  onChange,
  options,
  className,
  "aria-label": ariaLabel,
}) => {
  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      className={cn(
        "inline-flex items-center gap-1 rounded-lg border border-white/12 bg-white/5 p-1",
        className,
      )}
    >
      {options.map((option) => {
        const active = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => onChange(option.value)}
            className={cn(
              "min-w-[3rem] rounded-md px-3 py-1 text-sm font-medium transition-colors",
              active
                ? "bg-primary text-primary-foreground shadow-sm"
                : "text-muted-foreground hover:bg-white/5 hover:text-foreground",
            )}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
};
